import { ApiResult, HttpClient } from "../http.js";
import { KsDashboardDto, KsProjectItemDto } from "../types/ks-project-items.js";

export interface AssignKsTemplatePayload {
  projectId: string;
  projectTitle: string;
  templateId: string;
}

export interface CompleteKsItemPayload {
  value: unknown;
}

export interface OverrideKsItemPayload {
  reason: string;
}

export class KsProjectItemsApi {
  constructor(private readonly http: HttpClient) {}

  assign(payload: AssignKsTemplatePayload): Promise<ApiResult<string[]>> {
    return this.http.post<string[]>("/api/ks-project-items/assign", { body: payload });
  }

  listByProject(projectId: string): Promise<ApiResult<KsProjectItemDto[]>> {
    return this.http.get<KsProjectItemDto[]>("/api/ks-project-items", {
      query: { projectId },
    });
  }

  /** Org-wide KS counters, aggregated server-side. */
  dashboard(): Promise<ApiResult<KsDashboardDto>> {
    return this.http.get<KsDashboardDto>("/api/ks-project-items/dashboard");
  }

  get(id: string): Promise<ApiResult<KsProjectItemDto>> {
    return this.http.get<KsProjectItemDto>(`/api/ks-project-items/${encodeURIComponent(id)}`);
  }

  complete(id: string, payload: CompleteKsItemPayload): Promise<ApiResult<KsProjectItemDto>> {
    return this.http.post<KsProjectItemDto>(
      `/api/ks-project-items/${encodeURIComponent(id)}/complete`,
      { body: payload },
    );
  }

  override(id: string, payload: OverrideKsItemPayload): Promise<ApiResult<KsProjectItemDto>> {
    return this.http.post<KsProjectItemDto>(
      `/api/ks-project-items/${encodeURIComponent(id)}/override`,
      { body: payload },
    );
  }

  skip(id: string): Promise<ApiResult<KsProjectItemDto>> {
    return this.http.post<KsProjectItemDto>(
      `/api/ks-project-items/${encodeURIComponent(id)}/skip`,
    );
  }

  reset(id: string): Promise<ApiResult<KsProjectItemDto>> {
    return this.http.post<KsProjectItemDto>(
      `/api/ks-project-items/${encodeURIComponent(id)}/reset`,
    );
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/ks-project-items/${encodeURIComponent(id)}`);
  }

  /**
   * Deletes the project's KS items. Pass `templateId` to unassign a single
   * template — without it every KS item on the project is removed.
   */
  removeByProject(projectId: string, templateId?: string): Promise<ApiResult<number>> {
    return this.http.delete<number>("/api/ks-project-items", {
      query: { projectId, templateId },
    });
  }
}
